<!-- src/components/ErrorBoundary.vue -->
<!-- Vue Error Boundary component for graceful error handling -->
<!-- Catches component errors and displays fallback UI instead of crashing -->
<!-- RELEVANT FILES: src/main.ts, src/utils/sentry.ts -->

<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { AlertTriangle } from 'lucide-vue-next';
import { onErrorCaptured, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    fallbackMessage?: string;
    showDetails?: boolean;
  }>(),
  {
    fallbackMessage: 'Something went wrong',
    showDetails: false,
  },
);

// Reference props to satisfy TypeScript's noUnusedLocals
// Props are used in the template section
const { fallbackMessage, showDetails } = props;

const error = ref<Error | null>(null);
const errorInfo = ref<string>('');
const hasError = ref(false);

// Capture errors from child components
onErrorCaptured((err: Error, instance, info: string) => {
  hasError.value = true;
  error.value = err;
  errorInfo.value = info;

  // Send error to Sentry with additional context
  Sentry.captureException(err, {
    contexts: {
      vue: {
        componentName: instance?.$options.name || 'Unknown',
        propsData: instance?.$props,
        lifecycle: info,
      },
    },
  });

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('Error caught by ErrorBoundary:', err);
    console.error('Component info:', info);
  }

  // Prevent the error from propagating further
  return false;
});

function reset() {
  hasError.value = false;
  error.value = null;
  errorInfo.value = '';
}
</script>

<template>
  <div v-if="hasError" class="flex min-h-[400px] items-center justify-center p-8">
    <div
      class="w-full max-w-md rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950"
    >
      <div class="flex items-start gap-4">
        <AlertTriangle class="h-6 w-6 flex-shrink-0 text-red-600 dark:text-red-400" />
        <div class="flex-1 space-y-3">
          <h3 class="text-lg font-semibold text-red-900 dark:text-red-100">
            {{ fallbackMessage }}
          </h3>

          <p class="text-sm text-red-700 dark:text-red-300">
            An unexpected error occurred. Our team has been notified and will investigate.
          </p>

          <div v-if="showDetails && error" class="rounded bg-red-100 p-3 dark:bg-red-900">
            <p class="font-mono text-xs text-red-800 dark:text-red-200">
              {{ error.message }}
            </p>
          </div>

          <button
            type="button"
            class="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
            @click="reset"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Render children if no error -->
  <slot v-else />
</template>
